import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '@mantine/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';

/**
 * Drives the prev/next tab navigation that lives in the report StickyActionBar.
 *
 * The active-tab state stays inside each tabbed form (via {@link useTabNavigation}),
 * but the form pushes its navigation handlers up into this context so the shared
 * action bar — one level above the form — can render the Zurück/Weiter buttons.
 * Forms without tabs never register, so the buttons simply don't appear.
 */
interface TabNavState {
  canGoPrev: boolean;
  canGoNext: boolean;
  goPrev: () => void;
  goNext: () => void;
}

const EMPTY: TabNavState = {
  canGoPrev: false,
  canGoNext: false,
  goPrev: () => {},
  goNext: () => {},
};

const TabNavRegisterContext = createContext<(state: TabNavState | null) => void>(() => {});
const TabNavStateContext = createContext<TabNavState>(EMPTY);

export function TabNavProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TabNavState>(EMPTY);
  const register = useCallback((next: TabNavState | null) => {
    setState(next ?? EMPTY);
  }, []);
  return (
    <TabNavRegisterContext.Provider value={register}>
      <TabNavStateContext.Provider value={state}>{children}</TabNavStateContext.Provider>
    </TabNavRegisterContext.Provider>
  );
}

/** Read side — consumed by the action bar buttons. */
function useTabNav(): TabNavState {
  return useContext(TabNavStateContext);
}

/**
 * Tab state for a tabbed form. Owns the active tab and registers prev/next
 * navigation with the surrounding {@link TabNavProvider}.
 *
 * `tabValues` must have a stable identity across renders (declare it as a
 * module-level const) so the registration effect doesn't churn.
 */
export function useTabNavigation(tabValues: readonly string[]) {
  const register = useContext(TabNavRegisterContext);
  const [activeTab, setActiveTab] = useState<string | null>(tabValues[0] ?? null);

  const index = activeTab ? tabValues.indexOf(activeTab) : -1;
  const canGoPrev = index > 0;
  const canGoNext = index >= 0 && index < tabValues.length - 1;

  const goPrev = useCallback(() => {
    setActiveTab((cur) => {
      const i = cur ? tabValues.indexOf(cur) : -1;
      return i > 0 ? tabValues[i - 1] : cur;
    });
  }, [tabValues]);

  const goNext = useCallback(() => {
    setActiveTab((cur) => {
      const i = cur ? tabValues.indexOf(cur) : -1;
      return i >= 0 && i < tabValues.length - 1 ? tabValues[i + 1] : cur;
    });
  }, [tabValues]);

  useEffect(() => {
    register({ canGoPrev, canGoNext, goPrev, goNext });
    return () => register(null);
  }, [register, canGoPrev, canGoNext, goPrev, goNext]);

  return { activeTab, setActiveTab };
}

/** Left action: steps one tab back. Hidden on the first tab / non-tabbed forms. */
export function TabPrevButton({ disabled }: { disabled?: boolean }) {
  const { canGoPrev, goPrev } = useTabNav();
  if (!canGoPrev) return null;
  return (
    <Button
      variant="filled"
      leftSection={<IconArrowLeft size={16} />}
      onClick={goPrev}
      disabled={disabled}
    >
      Zurück
    </Button>
  );
}

/** Right action: steps one tab forward. Hidden on the last tab / non-tabbed forms. */
export function TabNextButton({ disabled }: { disabled?: boolean }) {
  const { canGoNext, goNext } = useTabNav();
  if (!canGoNext) return null;
  return (
    <Button
      variant="filled"
      rightSection={<IconArrowRight size={16} />}
      onClick={goNext}
      disabled={disabled}
    >
      Weiter
    </Button>
  );
}
